/**
 * The lucky draw's rules, shared by the console, the server and the wheel. Visitors spin as often
 * as they like; every spin is its own draw on the server: first treat or trick, by the draw's win
 * chance, then which treat or trick, by each segment's weight among its own kind.
 */

/** Who sees the wheel: as the ways to pay, staff only lets the owner try it on the live site. */
export const DRAW_OFFERS = ["off", "staff", "everyone"] as const
export type DrawOffer = (typeof DRAW_OFFERS)[number]

export const DRAW_OFFER_LABEL: Record<DrawOffer, string> = {
  off: "Off",
  staff: "Staff only, to test",
  everyone: "Everyone",
}

/** A discount is a code from Offers & codes; a gift ships free with their next order. */
export const PRIZE_KINDS = ["coupon", "gift", "nothing"] as const
export type PrizeKind = (typeof PRIZE_KINDS)[number]

export const PRIZE_KIND_LABEL: Record<PrizeKind, string> = {
  coupon: "Discount code",
  gift: "Free gift",
  nothing: "Nothing (a trick)",
}

export type Prize = {
  /** Stable across edits, so a spin keeps pointing at its segment. */
  id: string
  /** The big word on the wheel: "Treat", "Trick". */
  label: string
  /** Under it: "10% off", "Free keychain", "Try again". */
  detail: string
  kind: PrizeKind
  /** kind "coupon": a code in Offers & codes. */
  couponCode: string | null
  /** kind "gift": what is sent, as the order and the winner see it. */
  gift: string | null
  /** Share among its own kind (treats or tricks); 0 keeps it on the wheel without it landing. */
  weight: number
  /** Most wins per round; null for no limit. */
  maxWins: number | null
}

export type LuckyDrawSettings = {
  offer: DrawOffer
  /** Percent of spins that land on a treat (a discount or a gift); the rest are tricks. */
  winChance: number
  /** A visitor whose first spin of the round is a trick gets a treat on their second. */
  sureSecond: boolean
  /** Restarting the draw moves this on, and every segment's win limit starts again. */
  round: number
  title: string
  subtitle: string
  prizes: Prize[]
}

export const DEFAULT_WIN_CHANCE = 35

export const DEFAULT_DRAW: LuckyDrawSettings = {
  offer: "off",
  winChance: DEFAULT_WIN_CHANCE,
  sureSecond: true,
  round: 1,
  title: "Treat or trick?",
  subtitle: "Spin for a SKELMET surprise",
  prizes: [],
}

export const MIN_SEGMENTS = 2
export const MAX_SEGMENTS = 12

/** What the wheel shows: never the chances, codes or limits. */
export type WheelSegment = Pick<Prize, "id" | "label" | "detail" | "kind">

export const toSegment = ({ id, label, detail, kind }: Prize): WheelSegment => ({
  id,
  label,
  detail,
  kind,
})

/** The wheel as the storefront gets it; null while the draw is off. */
export type StorefrontDraw = {
  offer: Exclude<DrawOffer, "off">
  title: string
  subtitle: string
  segments: WheelSegment[]
}

/** What a spin won, as the visitor sees it. */
export type SpinResult = {
  prizeId: string
  label: string
  detail: string
  kind: PrizeKind
  couponCode: string | null
  gift: string | null
  claimCode: string | null
}

export const isTreat = (kind: PrizeKind) => kind !== "nothing"

/**
 * Picks by weight. `roll(total)` returns an integer in [0, total); the server passes a
 * cryptographic one. Null when nothing has any chance.
 */
export function pickPrize<T extends { weight: number }>(
  prizes: readonly T[],
  roll: (total: number) => number,
): T | null {
  const total = prizes.reduce((sum, p) => sum + Math.max(0, p.weight), 0)
  if (total <= 0) return null
  let r = roll(total)
  for (const prize of prizes) {
    const weight = Math.max(0, prize.weight)
    if (r < weight) return prize
    r -= weight
  }
  return null
}

/**
 * Each segment's chance in percent, as the console shows it: its share of the win chance among
 * treats, or of the rest among tricks.
 */
export function chances(
  prizes: readonly Pick<Prize, "id" | "weight" | "kind">[],
  winChance: number,
): Record<string, number> {
  const total = (treat: boolean) =>
    prizes
      .filter((p) => isTreat(p.kind) === treat)
      .reduce((sum, p) => sum + Math.max(0, p.weight), 0)
  const treats = total(true)
  const tricks = total(false)
  return Object.fromEntries(
    prizes.map((p) => {
      const treat = isTreat(p.kind)
      const pool = treat ? treats : tricks
      const share = treat ? winChance : 100 - winChance
      return [p.id, pool > 0 ? (Math.max(0, p.weight) / pool) * share : 0]
    }),
  )
}

/**
 * One spin: a treat with `winChance` percent (always, with `sure`), else a trick, then which one
 * by weight. A treat that cannot be had (`treats` empty) falls to a trick. `roll(n)` returns an
 * integer in [0, n).
 */
export function drawPrize<T extends { weight: number }>(
  treats: readonly T[],
  tricks: readonly T[],
  winChance: number,
  roll: (total: number) => number,
  { sure = false }: { sure?: boolean } = {},
): T | null {
  // Hundredths of a percent, so 35 and 35.5 both draw exactly.
  const win = sure || roll(10_000) < Math.round(winChance * 100)
  return (win ? pickPrize(treats, roll) : null) ?? pickPrize(tricks, roll)
}

/**
 * The wheel's rotation (degrees, clockwise) that brings segment `index` under the pointer at the
 * top, `turns` full turns on from `from`. Segment i spans [i, i + 1) × 360 / count clockwise from
 * the top; `at` (0..1) is where in it the pointer stops.
 */
export function landingAngle(
  index: number,
  count: number,
  from: number,
  { turns = 6, at = 0.5 }: { turns?: number; at?: number } = {},
): number {
  const seg = 360 / count
  const target = 360 - (index + at) * seg
  const rest = (((target - from) % 360) + 360) % 360
  return from + turns * 360 + rest
}

/** The segment under the pointer at a rotation, for tests and the resting wheel. */
export function segmentAt(rotation: number, count: number): number {
  const seg = 360 / count
  const under = (((360 - rotation) % 360) + 360) % 360
  return Math.floor(under / seg) % count
}
